/**
 * Utilities for deriving proxy vs original specs from asset metadata, plus the
 * spec-line formatter used by the expanded image/video modals.
 */

import type { Asset } from '@/types'
import { isHyperionVideoModel } from '@/types/shared'

/** Parsed specs for one side (proxy or original). */
export interface MediaSpecs {
  width: number
  height: number
  /** Bits per color sample. Omitted from display for ordinary 8-bit SDR. */
  bitDepth?: number
  /** Human-readable format/codec label (e.g. "H.264", "ProRes", "JPEG") */
  format: string
  /** File size in bytes */
  fileSizeBytes?: number
  /** True when this side represents an HDR source (drives the HDR color token). */
  isHdr?: boolean
  /** Normalized color transfer characteristic, e.g. 'smpte2084' (PQ), 'arib-std-b67' (HLG). */
  transfer?: string
}

export interface ProxyOriginalSpecs {
  proxy: MediaSpecs | null
  original: MediaSpecs | null
}

/** Map of common ffprobe codec_name values to human-readable labels. */
const CODEC_LABELS: Record<string, string> = {
  h264: 'H.264',
  hevc: 'H.265',
  prores: 'ProRes',
  vp9: 'VP9',
  av1: 'AV1',
  mpeg4: 'MPEG-4',
  vp8: 'VP8',
  dnxhd: 'DNxHD',
  cfhd: 'CineForm',
}

/** Map of MIME types to human-readable format labels. */
const MIME_LABELS: Record<string, string> = {
  'video/mp4': 'MP4',
  'video/quicktime': 'MOV',
  'video/webm': 'WebM',
  'video/x-matroska': 'MKV',
  'video/mp2t': 'TS',
  'video/x-msvideo': 'AVI',
  'image/jpeg': 'JPEG',
  'image/png': 'PNG',
  'image/webp': 'WebP',
  'image/gif': 'GIF',
  'image/tiff': 'TIFF',
  'image/bmp': 'BMP',
}

/** Extract the proxy resolution height from a proxy URI (e.g. 720 from "-720p-proxy.mp4"). */
export function parseProxyResolution(proxyUri: string): number | null {
  const match = proxyUri.match(/-(\d+)p-proxy\./)
  return match ? parseInt(match[1], 10) : null
}

/** Compute proxy dimensions from proxy height and original aspect ratio. */
export function computeProxyDimensions(
  proxyHeight: number,
  originalWidth: number,
  originalHeight: number,
): { width: number; height: number } {
  const width = Math.round((originalWidth / originalHeight) * proxyHeight)
  // H.264 requires even dimensions
  return { width: width % 2 === 0 ? width : width - 1, height: proxyHeight }
}

/** Convert a MIME type to a human-readable label. */
export function mimeToLabel(mimeType: string): string {
  return MIME_LABELS[mimeType] || mimeType.split('/').pop()?.toUpperCase() || 'Unknown'
}

/** Convert an ffprobe codec_name to a human-readable label. */
export function codecToLabel(codec: string): string {
  return CODEC_LABELS[codec.toLowerCase()] || codec.toUpperCase()
}

/** Format bytes into a human-readable string (KB, MB, GB). */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`
}

/** Build the HDR color token, folding bit depth in, e.g. "10-bit HDR (PQ)". */
function formatHdrToken(bitDepth?: number, transfer?: string): string {
  const depth = bitDepth && bitDepth >= 10 ? `${bitDepth}-bit ` : ''
  const t = transfer?.toLowerCase()
  const variant = t === 'smpte2084' || t === 'pq' ? ' (PQ)' : t === 'arib-std-b67' || t === 'hlg' ? ' (HLG)' : ''
  return `${depth}HDR${variant}`
}

/** Format a single spec line like "720 × 405 • H.264" or "1024 × 1024 • 320 KB • JPEG". */
export function formatSpecLine(spec: {
  width: number
  height: number
  bitDepth?: number
  format: string
  fileSizeBytes?: number
  isHdr?: boolean
  transfer?: string
}): string {
  const parts = [`${spec.width} × ${spec.height}`]
  // Bit depth shows standalone only for non-HDR; HDR folds it into the color token below.
  if (!spec.isHdr && spec.bitDepth && spec.bitDepth >= 10) parts.push(`${spec.bitDepth}-bit`)
  if (spec.fileSizeBytes) parts.push(formatFileSize(spec.fileSizeBytes))
  parts.push(spec.format)
  if (spec.isHdr) parts.push(formatHdrToken(spec.bitDepth, spec.transfer))
  return parts.join(' · ')
}

/**
 * Single source of truth for "is this asset's source HDR" — consumed by the
 * canvas badge, the spec line, and the expanded modal so all three agree.
 *
 * - Hyperion upscales (hyp-1 / hyp-2 / hyp-2.5) convert SDR->HDR; their 10-bit
 *   output may not carry a PQ/HLG transfer tag, so they are HDR by model.
 * - Otherwise trust the probed source color when present.
 * - For legacy assets without probed color, fall back to the upscale bit depth.
 */
export function assetIsHdr(asset: Asset): boolean {
  const model = asset.upscaleModel || asset.upscaleParams?.model
  if (model && isHyperionVideoModel(model)) return true
  if (asset.sourceColor) return asset.sourceColor.isHdr
  const bitDepth = asset.upscaleParams?.videoBitDepth
  return typeof bitDepth === 'number' && bitDepth >= 10
}

/**
 * Derive proxy and original specs from an asset's metadata.
 * Returns null values when insufficient data is available.
 */
export function getProxySpecs(asset: Asset): ProxyOriginalSpecs {
  const isVideo = asset.mimeType?.startsWith('video/') || asset.assetType === 'video'

  // Original specs
  let original: MediaSpecs | null = null
  if (asset.width && asset.height) {
    const format = asset.codec ? codecToLabel(asset.codec) : mimeToLabel(asset.mimeType)
    original = {
      width: asset.width,
      height: asset.height,
      // Prefer probed source color; fall back to the upscale-only bit depth for legacy assets.
      bitDepth: asset.sourceColor?.bitDepth ?? asset.upscaleParams?.videoBitDepth,
      format,
      fileSizeBytes: asset.fileSizeBytes,
      isHdr: assetIsHdr(asset),
      transfer: asset.sourceColor?.transfer,
    }
  }

  // Proxy specs — VIDEO ONLY. The 480p/720p preview-quality setting rewrites the
  // H.264 video proxy URI; images use a separate size/DPR thumbnail ladder (webp)
  // and we don't track the exact displayed tier here, so we don't synthesize a
  // (misleading) image "proxy" spec. asset.proxyUri on an image is its video proxy.
  let proxy: MediaSpecs | null = null
  if (isVideo && asset.proxyUri && asset.width && asset.height) {
    const proxyHeight = parseProxyResolution(asset.proxyUri)
    if (proxyHeight) {
      const dims = computeProxyDimensions(proxyHeight, asset.width, asset.height)
      proxy = { width: dims.width, height: dims.height, format: 'H.264' }
    }
  }

  return { proxy, original }
}
